import { Outlet, useLocation, Link } from 'react-router-dom'
import { Suspense, useState, useCallback, useEffect, useMemo } from 'react'
import Sidebar from '@/components/layout/Sidebar'
import Header from '@/components/layout/Header'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import NoPermission from '@/pages/dashboard/NoPermission'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { resolvePathAccess } from '@/lib/dashboardAccess'
import { releaseStuckModalOverlays } from '@/lib/modalOverlayCleanup'
import {
  loadSidebarDesktopCollapsed,
  persistSidebarDesktopCollapsed,
  sidebarDesktopCollapsedStorageKey,
} from '@/components/layout/sidebarNavConfig'

import { useSessionManager } from '@/hooks/useSessionManager'
import { useSupportInboxAlerts } from '@/hooks/useSupportInboxAlerts'
import { PlatformSupportWidget } from '@/components/support/PlatformSupportWidget'
import { platformBillingService } from '@/services/platformBillingService'
import { planEntitlementValue, ShopWriteLockProvider } from '@/hooks/useShopWriteLock'
import { useShopPreferences } from '@/hooks/useShopPreferences'
import { formatDateTime as formatDateTimeUtil } from '@/lib/utils'
import type { CSSProperties } from 'react'
import { platformNotificationsService, type PlatformNotification } from '@/services/platformNotificationsService'

interface DashboardLayoutProps {
  isCustomer?: boolean
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ isCustomer = false }) => {
  useSessionManager()
  useSupportInboxAlerts({ isCustomer })
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const location = useLocation()
  const { user } = useAuthStore()
  const { permissions, role, isLoading, loadPermissions } = usePermissionsStore()
  const { shop, isLoading: shopLoading } = useShopPreferences()
  const [writesAllowed, setWritesAllowed] = useState(true)
  const [writeLockLoading, setWriteLockLoading] = useState(false)
  const [planCode, setPlanCode] = useState('trial')
  const [planFeatures, setPlanFeatures] = useState<string[]>([])
  const [activePlatformNotices, setActivePlatformNotices] = useState<PlatformNotification[]>([])

  const dismissedNoticesStorageKey = useMemo(() => {
    if (!user?.shop_id) return null
    return `platform_notifications_dismissed_${user.shop_id}`
  }, [user?.shop_id])

  const [dismissedNoticeIds, setDismissedNoticeIds] = useState<number[]>([])

  useEffect(() => {
    if (!dismissedNoticesStorageKey) {
      setDismissedNoticeIds([])
      return
    }
    if (typeof window === 'undefined') return

    try {
      const raw = window.localStorage.getItem(dismissedNoticesStorageKey)
      if (!raw) {
        setDismissedNoticeIds([])
        return
      }
      const parsed = JSON.parse(raw)
      setDismissedNoticeIds(Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'number') : [])
    } catch {
      setDismissedNoticeIds([])
    }
  }, [dismissedNoticesStorageKey])

  const dismissPlatformNotice = useCallback(
    (notice: PlatformNotification) => {
      if (!notice.is_dismissible) return
      setDismissedNoticeIds((prev) => {
        if (prev.includes(notice.notification_id)) return prev
        const next = [...prev, notice.notification_id]
        if (dismissedNoticesStorageKey && typeof window !== 'undefined') {
          try {
            window.localStorage.setItem(dismissedNoticesStorageKey, JSON.stringify(next))
          } catch {
            // If storage fails, still hide the banner for the current session.
          }
        }
        return next
      })
    },
    [dismissedNoticesStorageKey],
  )

  const visiblePlatformNotices = useMemo(() => {
    if (dismissedNoticeIds.length === 0) return activePlatformNotices
    const dismissed = new Set(dismissedNoticeIds)
    return activePlatformNotices.filter((n) => !dismissed.has(n.notification_id))
  }, [activePlatformNotices, dismissedNoticeIds])

  const customerPortalShellStyle = useMemo((): CSSProperties | undefined => {
    if (!isCustomer) return undefined
    const primary = String(shop?.primary_color || '').trim() || '#4f46e5'
    const secondary = String(shop?.secondary_color || '').trim() || primary
    return {
      ['--shop-primary' as string]: primary,
      ['--shop-secondary' as string]: secondary,
      ['--shop-primary-soft' as string]: `color-mix(in srgb, ${primary} 12%, white)`,
      ['--shop-primary-muted' as string]: `color-mix(in srgb, ${primary} 22%, white)`,
    }
  }, [isCustomer, shop?.primary_color, shop?.secondary_color])

  const collapsedStorageKey = useMemo(
    () => sidebarDesktopCollapsedStorageKey(isCustomer ? 'customer' : 'staff'),
    [isCustomer],
  )
  const [desktopCollapsed, setDesktopCollapsed] = useState(() =>
    loadSidebarDesktopCollapsed(collapsedStorageKey),
  )

  useEffect(() => {
    setDesktopCollapsed(loadSidebarDesktopCollapsed(collapsedStorageKey))
  }, [collapsedStorageKey])

  useEffect(() => {
    if (isCustomer || !user?.shop_id || user.role === 'super_admin') {
      setWritesAllowed(true)
      setWriteLockLoading(false)
      return
    }
    let cancelled = false
    setWriteLockLoading(true)
    void platformBillingService
      .getBilling(user.shop_id)
      .then((res) => {
        if (cancelled) return
        const allowed = res.data?.writes_allowed ?? res.data?.subscription_active ?? true
        setWritesAllowed(Boolean(allowed))
        setPlanCode(String(res.data?.plan_code || 'trial'))
        setPlanFeatures(Array.isArray(res.data?.features) ? res.data.features : [])
      })
      .catch(() => {
        if (!cancelled) setWritesAllowed(true)
      })
      .finally(() => {
        if (!cancelled) setWriteLockLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isCustomer, user?.shop_id, user?.role, location.pathname])

  // Platform-wide notices (maintenance reminders, etc.) shown to tenant staff + customer portal.
  useEffect(() => {
    if (!user?.shop_id) return
    let cancelled = false

    platformNotificationsService
      .active()
      .then((res) => {
        if (cancelled) return
        setActivePlatformNotices(Array.isArray(res.data) ? res.data : [])
      })
      .catch(() => {
        if (cancelled) return
        setActivePlatformNotices([])
      })

    return () => {
      cancelled = true
    }
  }, [user?.shop_id, isCustomer])

  const handleDesktopCollapsedChange = useCallback(
    (collapsed: boolean) => {
      setDesktopCollapsed(collapsed)
      persistSidebarDesktopCollapsed(collapsedStorageKey, collapsed)
      if (!collapsed) setSidebarOpen(false)
    },
    [collapsedStorageKey],
  )

  /** Mobile: open overlay flyout. Desktop (collapsed): restore the pinned sidebar. */
  const handleMenuClick = useCallback(() => {
    if (desktopCollapsed && window.matchMedia('(min-width: 768px)').matches) {
      handleDesktopCollapsedChange(false)
      return
    }
    setSidebarOpen(true)
  }, [desktopCollapsed, handleDesktopCollapsedChange])

  useEffect(() => {
    if (isCustomer || !user?.shop_id) return
    void loadPermissions(user.shop_id)
  }, [isCustomer, user?.shop_id, loadPermissions])

  // Close flyout + drop any orphan modal scrims when navigating between dashboard pages.
  useEffect(() => {
    closeSidebar()
    releaseStuckModalOverlays()
  }, [location.pathname, closeSidebar])

  // bfcache can restore a tab with a stuck Headless UI backdrop still on document.body.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        closeSidebar()
        releaseStuckModalOverlays()
      }
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [closeSidebar])

  // Compact typography for admin/customer dashboard — does not affect public shop pages.
  useEffect(() => {
    document.documentElement.classList.add('oms-dashboard')
    return () => document.documentElement.classList.remove('oms-dashboard')
  }, [])

  // Keep layout/sidebar mounted while lazy route chunks load (Suspense must wrap Outlet, not App Routes).
  let mainContent: React.ReactNode = (
    <Suspense
      fallback={
        <div className="min-h-[50vh] flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  )

  if (!isCustomer && user?.shop_id) {
    const operatorPath = location.pathname.startsWith('/dashboard/admin/support')
    if ((isLoading && !permissions) || (operatorPath && shopLoading && user.role !== 'super_admin')) {
      mainContent = (
        <div className="min-h-[50vh] flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      )
    } else {
      const access = resolvePathAccess(location.pathname, {
        role: role ?? user.role,
        permissions,
        isPlatformShop: shop?.is_platform_shop === true,
        planCode,
        planFeatures,
      })
      if (!access.allowed) {
        mainContent = <NoPermission moduleLabel={access.label} reason={access.reason} />
      }
    }
  }

  const header = (
    <Header
      onMenuClick={handleMenuClick}
      desktopCollapsed={desktopCollapsed}
      isCustomer={isCustomer}
    />
  )

  const mainPane = (
    <>
      {visiblePlatformNotices[0] ? (
        <div
          className={[
            'shrink-0 flex items-start justify-between gap-4 rounded-md border px-4 py-3 text-sm shadow-sm',
            visiblePlatformNotices[0].severity === 'danger'
              ? 'border-red-200 bg-red-50 text-red-950 border-l-4 border-l-red-500'
              : visiblePlatformNotices[0].severity === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-950 border-l-4 border-l-amber-500'
                : 'border-slate-200 bg-slate-50 text-slate-950 border-l-4 border-l-slate-500',
          ].join(' ')}
          role="status"
          aria-live="polite"
        >
          <div className="min-w-0">
            <div className="flex items-start gap-3">
              <span
                className={[
                  'mt-1 h-2.5 w-2.5 flex-none rounded-full',
                  visiblePlatformNotices[0].severity === 'danger'
                    ? 'bg-red-500'
                    : visiblePlatformNotices[0].severity === 'warning'
                      ? 'bg-amber-500'
                      : 'bg-slate-500',
                ].join(' ')}
                aria-hidden="true"
              />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2">
                  <strong className="font-semibold">{visiblePlatformNotices[0].title}</strong>
                </div>
                <div className="mt-1 whitespace-pre-wrap">{visiblePlatformNotices[0].message}</div>
                <div className="mt-1 text-xs opacity-80">
                  Maintenance: {formatDateTimeUtil(visiblePlatformNotices[0].maintenance_start_at)} →{' '}
                  {formatDateTimeUtil(visiblePlatformNotices[0].maintenance_end_at)}
                </div>
              </div>
            </div>
          </div>

          {visiblePlatformNotices[0].is_dismissible ? (
            <button
              type="button"
              onClick={() => dismissPlatformNotice(visiblePlatformNotices[0])}
              className={[
                'mt-0.5 shrink-0 rounded p-1',
                visiblePlatformNotices[0].severity === 'danger'
                  ? 'text-red-700 hover:bg-red-100 hover:text-red-900'
                  : visiblePlatformNotices[0].severity === 'warning'
                    ? 'text-amber-700 hover:bg-amber-100 hover:text-amber-900'
                    : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
              ].join(' ')}
              aria-label="Dismiss notification"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}
      {!isCustomer && !writesAllowed ? (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950">
          <strong>View only.</strong> This shop’s ShopQuay subscription or trial is not active.
          You can browse data, but create and edit are blocked.{' '}
          <Link to="/dashboard/settings/subscription" className="font-semibold underline">
            Renew or choose a plan
          </Link>
          .
        </div>
      ) : null}

      <main className="flex-1 relative overflow-y-auto focus:outline-none">
        <div className="py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="max-w-[1600px] w-full min-w-0 mx-auto pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))]">
            {mainContent}
          </div>
        </div>
      </main>
    </>
  )

  return (
    <ShopWriteLockProvider
      value={planEntitlementValue({
        writesAllowed,
        loading: writeLockLoading,
        planCode,
        features: planFeatures,
      })}
    >
      <div
        className={[
          'h-screen overflow-hidden bg-gray-100 supports-[height:100dvh]:h-dvh',
          isCustomer ? 'customer-portal-shell flex flex-col' : 'flex',
        ].join(' ')}
        style={customerPortalShellStyle}
      >
        {isCustomer ? (
          <>
            {header}
            <div className="flex min-h-0 flex-1 overflow-hidden">
              <Sidebar
                isOpen={sidebarOpen}
                onClose={closeSidebar}
                isCustomer
                desktopCollapsed={desktopCollapsed}
                onDesktopCollapsedChange={handleDesktopCollapsedChange}
              />
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">{mainPane}</div>
            </div>
          </>
        ) : (
          <>
            <Sidebar
              isOpen={sidebarOpen}
              onClose={closeSidebar}
              desktopCollapsed={desktopCollapsed}
              onDesktopCollapsedChange={handleDesktopCollapsedChange}
            />
            <div
              className={[
                'flex flex-col w-0 flex-1 overflow-hidden transition-[padding] duration-200 ease-out',
                desktopCollapsed ? 'md:pl-0' : 'md:pl-64',
              ].join(' ')}
            >
              {header}
              {mainPane}
            </div>
            <PlatformSupportWidget />
          </>
        )}
      </div>
    </ShopWriteLockProvider>
  )
}

export default DashboardLayout
